import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { FlaskConical, Mail, Plus, Send, Trash2, Webhook } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { statusesOf } from '@/features/stages/stages';
import {
  automationsCreate,
  automationsDelete,
  automationsGet,
  automationsTest,
  automationsUpdate,
} from '@/shared/api/generated/automations/automations';
import { contentTypesGet } from '@/shared/api/generated/content/content';
import type {
  ActionBody,
  ActionResult,
  AutomationBody,
  RunBody,
} from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { describeWhen } from '@/shared/lib/dates';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import {
  ACTION_TITLES,
  blankAction,
  inputOf,
  parseRecipients,
  type ActionKind,
} from './automations';
import styles from './AutomationsPage.module.scss';

const COMMON = ['title', 'collection', 'site', 'stage', 'comment', 'link', 'fields'];

/** Новая автоматизация. */
export function NewAutomationPage() {
  return <AutomationEditor automation={undefined} />;
}

/** Автоматизация: правка, проверка и журнал. */
export function AutomationPage() {
  const { automationId } = useParams({
    from: '/authed/sites/$siteId/$env/automations/$automationId',
  });

  const detail = useQuery({
    queryKey: ['automation', automationId],
    queryFn: async () => {
      const response = await automationsGet(automationId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на автоматизацию');
      }

      return response.data;
    },
  });

  if (detail.data === undefined) {
    return detail.isError ? <p className={catalog['empty']}>Такой автоматизации нет.</p> : null;
  }

  return (
    <AutomationEditor
      key={detail.data.automation.etag}
      automation={detail.data.automation}
      runs={detail.data.runs ?? []}
    />
  );
}

function AutomationEditor({
  automation,
  runs = [],
}: {
  automation: AutomationBody | undefined;
  runs?: RunBody[];
}) {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useSiteScope();
  const collections = (scope?.types ?? []).filter((t) => t.kind === 'collection');

  const [name, setName] = useState(automation?.name ?? 'Письмо о новой заявке');
  const [enabled, setEnabled] = useState(automation?.enabled ?? true);
  const [typeId, setTypeId] = useState(
    automation?.trigger.type_id ??
      collections.find((t) => t.purpose === 'form')?.id ??
      collections[0]?.id ??
      '',
  );
  const [event, setEvent] = useState(automation?.trigger.event ?? 'record.created');
  const [fromSite, setFromSite] = useState(automation?.trigger.from_site ?? true);
  const [stage, setStage] = useState(automation?.trigger.stage ?? '');
  const [actions, setActions] = useState<ActionBody[]>(
    automation?.actions ?? [blankAction('email')],
  );
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ActionResult[]>();

  const type = collections.find((t) => t.id === typeId);
  const statuses = statusesOf(type?.workflow);

  // Поля коллекции — для подстановок и «почты из поля».
  const fields = useQuery({
    queryKey: ['content-type', typeId],
    enabled: typeId !== '',
    queryFn: async () => {
      const response = await contentTypesGet(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на поля');
      }

      return response.data.fields ?? [];
    },
  });

  const keys = (fields.data ?? []).filter((f) => f.parent_id === undefined).map((f) => f.key);
  const placeholders = [...COMMON, ...keys.map((k) => `field.${k}`)];

  const patch = (index: number, next: Partial<ActionBody>) => {
    setActions((list) => list.map((a, i) => (i === index ? { ...a, ...next } : a)));
  };

  const body = () =>
    inputOf(
      name,
      enabled,
      {
        event,
        type_id: typeId,
        ...(fromSite ? { from_site: true } : {}),
        ...(event === 'record.stage' && stage !== '' ? { stage } : {}),
      },
      actions,
    );

  const save = () => {
    setBusy(true);

    const request =
      automation === undefined
        ? automationsCreate(siteId, kinds[env], body())
        : automationsUpdate(automation.id, body(), { 'If-Match': automation.etag });

    request
      .then((response) => {
        const saved = response.data as AutomationBody;

        toast.success('Сохранено');
        void queryClient.invalidateQueries({ queryKey: ['automations', siteId] });
        void queryClient.invalidateQueries({ queryKey: ['automation', saved.id] });

        if (automation === undefined) {
          void navigate({
            to: '/sites/$siteId/$env/automations/$automationId',
            params: { siteId, env, automationId: saved.id },
          });
        }
      })
      .catch((cause: unknown) => {
        toast.error('Не сохранилось', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const test = () => {
    if (automation === undefined) {
      return;
    }

    setBusy(true);
    setResults(undefined);

    automationsTest(automation.id, {})
      .then((response) => {
        setResults((response.data as { results?: ActionResult[] }).results ?? []);
        void queryClient.invalidateQueries({ queryKey: ['automation', automation.id] });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Проверка не удалась');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const remove = () => {
    if (automation === undefined) {
      return;
    }

    automationsDelete(automation.id)
      .then(() => {
        toast.success('Автоматизация удалена');
        void queryClient.invalidateQueries({ queryKey: ['automations', siteId] });
        void navigate({ to: '/sites/$siteId/$env/automations', params: { siteId, env } });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось удалить');
      });
  };

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={automation === undefined ? 'Новая автоматизация' : automation.name}
        subtitle={`Своя у ${env === 'dev' ? 'Development' : 'Production'}: на другом окружении её нет.`}
        actions={
          <>
            {automation !== undefined && (
              <Button
                variant="outline"
                tone="neutral"
                iconLeft={<FlaskConical size={15} />}
                disabled={busy}
                onClick={test}
              >
                Проверить
              </Button>
            )}
            <Button loading={busy} onClick={save}>
              Сохранить
            </Button>
          </>
        }
      />

      <div className={styles['editor']}>
        {results !== undefined && (
          <section className={styles['results']}>
            <h2 className={styles['title']}>Проверка на последней записи</h2>
            <Results results={results} />
          </section>
        )}

        <div className={styles['row']}>
          <Field label="Название">
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Field>
          <Switch checked={enabled} onCheckedChange={setEnabled}>
            Включена
          </Switch>
        </div>

        <section className={styles['section']}>
          <h2 className={styles['title']}>Когда</h2>
          <div className={styles['row']}>
            <Field label="Коллекция">
              <Select
                value={typeId}
                onValueChange={(value) => {
                  setTypeId(value);
                  setStage('');
                }}
              >
                {collections.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.purpose === 'form'
                      ? ' — форма'
                      : t.purpose === 'users'
                        ? ' — посетители'
                        : ''}
                  </SelectItem>
                ))}
              </Select>
            </Field>
            <Field label="Событие">
              <Segmented
                value={event}
                onValueChange={(value) => {
                  setEvent(value as typeof event);
                }}
                aria-label="Событие"
                options={[
                  { value: 'record.created', label: 'Новая запись' },
                  ...(statuses.length > 0
                    ? [{ value: 'record.stage', label: 'Сменился статус' }]
                    : []),
                ]}
              />
            </Field>
          </div>
          {event === 'record.stage' && (
            <Field label="В какой статус">
              <Select
                value={stage === '' ? 'any' : stage}
                onValueChange={(v) => {
                  setStage(v === 'any' ? '' : v);
                }}
              >
                <SelectItem value="any">В любой</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.name}
                  </SelectItem>
                ))}
              </Select>
            </Field>
          )}
          <Switch checked={fromSite} onCheckedChange={setFromSite}>
            Только с сайта — заявки, регистрации и действия посетителей, не правки команды в админке
          </Switch>
        </section>

        <section className={styles['section']}>
          <h2 className={styles['title']}>Что сделать</h2>
          <p className={styles['muted']}>
            По порядку. В тексте можно подставить:{' '}
            {placeholders.map((p) => (
              <code key={p} className={styles['chip']}>{`{{${p}}}`}</code>
            ))}
          </p>

          {actions.map((action, index) => (
            <ActionCard
              key={index}
              action={action}
              fieldKeys={keys}
              onChange={(next) => {
                patch(index, next);
              }}
              onKind={(kind) => {
                setActions((list) => list.map((a, i) => (i === index ? blankAction(kind) : a)));
              }}
              onRemove={
                actions.length > 1
                  ? () => {
                      setActions((list) => list.filter((_, i) => i !== index));
                    }
                  : undefined
              }
            />
          ))}

          {actions.length < 10 && (
            <div className={styles['add']}>
              {(['email', 'telegram', 'webhook'] as const).map((kind) => (
                <Button
                  key={kind}
                  variant="outline"
                  tone="neutral"
                  iconLeft={<Plus size={15} />}
                  onClick={() => {
                    setActions((list) => [...list, blankAction(kind)]);
                  }}
                >
                  {ACTION_TITLES[kind]}
                </Button>
              ))}
            </div>
          )}
        </section>

        {automation !== undefined && (
          <section className={styles['section']}>
            <h2 className={styles['title']}>Журнал запусков</h2>
            {runs.length === 0 ? (
              <p className={styles['muted']}>Ещё не запускалась.</p>
            ) : (
              <ul className={styles['runs']}>
                {runs.map((run) => (
                  <li key={run.id} className={styles['run']}>
                    <span className={styles['muted']}>{describeWhen(run.created_at)}</span>
                    <Results results={run.results ?? []} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {automation !== undefined && (
          <div>
            <Button variant="ghost" tone="danger" iconLeft={<Trash2 size={15} />} onClick={remove}>
              Удалить автоматизацию
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const ICONS: Record<ActionKind, typeof Mail> = { email: Mail, telegram: Send, webhook: Webhook };

function ActionCard({
  action,
  fieldKeys,
  onChange,
  onKind,
  onRemove,
}: {
  action: ActionBody;
  fieldKeys: string[];
  onChange: (next: Partial<ActionBody>) => void;
  onKind: (kind: ActionKind) => void;
  onRemove: (() => void) | undefined;
}) {
  const Icon = ICONS[action.kind];
  const to = action.to ?? [];

  return (
    <div className={styles['card']}>
      <div className={styles['card__head']}>
        <Icon size={16} />
        <Segmented
          value={action.kind}
          onValueChange={(v) => {
            onKind(v as ActionKind);
          }}
          aria-label="Вид действия"
          options={(['email', 'telegram', 'webhook'] as const).map((k) => ({
            value: k,
            label: ACTION_TITLES[k],
          }))}
        />
        {onRemove !== undefined && (
          <Button
            size="sm"
            variant="ghost"
            tone="danger"
            iconOnly
            aria-label="Убрать действие"
            className={styles['card__remove']}
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>

      {action.kind === 'email' && (
        <>
          <Field
            label="Кому"
            hint="Адреса через запятую. owner — владельцу записи (посетителю); field:ключ — на почту из поля записи."
          >
            <Input
              value={to.join(', ')}
              placeholder="team@example.com, owner"
              onChange={(e) => {
                onChange({ to: parseRecipients(e.target.value) });
              }}
            />
          </Field>
          <div className={styles['chips']}>
            {!to.includes('owner') && (
              <button
                type="button"
                className={styles['chip']}
                onClick={() => {
                  onChange({ to: [...to, 'owner'] });
                }}
              >
                + владельцу записи
              </button>
            )}
            {fieldKeys
              .filter((k) => !to.includes(`field:${k}`) && /mail|почт/i.test(k))
              .map((k) => (
                <button
                  key={k}
                  type="button"
                  className={styles['chip']}
                  onClick={() => {
                    onChange({ to: [...to, `field:${k}`] });
                  }}
                >
                  + на почту из поля «{k}»
                </button>
              ))}
          </div>
          <Field label="Тема">
            <Input
              value={action.subject ?? ''}
              onChange={(e) => {
                onChange({ subject: e.target.value });
              }}
            />
          </Field>
          <Field label="Текст">
            <Textarea
              rows={6}
              value={action.body ?? ''}
              onChange={(e) => {
                onChange({ body: e.target.value });
              }}
            />
          </Field>
        </>
      )}

      {action.kind === 'telegram' && (
        <>
          <div className={styles['row']}>
            <Field
              label="Токен бота"
              hint={
                action.bot_token_set === true
                  ? 'Задан. Оставьте пустым — останется прежний.'
                  : 'Выдаёт @BotFather. Добавьте бота в чат или напишите ему.'
              }
            >
              <Input
                type="password"
                autoComplete="off"
                value={action.bot_token ?? ''}
                placeholder={action.bot_token_set === true ? '••••••••' : '123456789:AA…'}
                onChange={(e) => {
                  onChange({ bot_token: e.target.value });
                }}
              />
            </Field>
            <Field label="Чат" hint="id чата (узнать — у @userinfobot) или @канал">
              <Input
                value={action.chat_id ?? ''}
                onChange={(e) => {
                  onChange({ chat_id: e.target.value });
                }}
              />
            </Field>
          </div>
          <Field label="Текст">
            <Textarea
              rows={5}
              value={action.body ?? ''}
              onChange={(e) => {
                onChange({ body: e.target.value });
              }}
            />
          </Field>
        </>
      )}

      {action.kind === 'webhook' && (
        <div className={styles['row']}>
          <Field
            label="Адрес"
            hint="POST с JSON записи (без скрытых полей) и заголовком X-Faceta-Event."
          >
            <Input
              value={action.url ?? ''}
              placeholder="https://example.com/hooks/faceta"
              onChange={(e) => {
                onChange({ url: e.target.value });
              }}
            />
          </Field>
          <Field
            label="Ключ подписи"
            hint={
              action.secret_set === true
                ? 'Задан. Оставьте пустым — останется прежний.'
                : 'Необязательно: X-Faceta-Signature: sha256=HMAC тела этим ключом.'
            }
          >
            <Input
              type="password"
              autoComplete="off"
              value={action.secret ?? ''}
              placeholder={action.secret_set === true ? '••••••••' : ''}
              onChange={(e) => {
                onChange({ secret: e.target.value });
              }}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

function Results({ results }: { results: ActionResult[] }) {
  return (
    <ul className={styles['results__list']}>
      {results.map((r, i) => (
        <li key={i}>
          <Badge size="sm" tone={r.ok ? 'success' : 'danger'}>
            {ACTION_TITLES[r.kind as ActionKind]}
          </Badge>{' '}
          <span className={r.ok ? undefined : styles['error']}>{r.detail}</span>
        </li>
      ))}
    </ul>
  );
}
